import { useEffect, useRef } from 'react';
import { getWebApp } from './telegram';

type Options = {
  text: string;
  onClick: () => void;
  visible?: boolean; // default true
  enabled?: boolean; // default true
  progress?: boolean; // показать спиннер
};

/**
 * Управляет нативной MainButton Telegram WebApp на время жизни компонента и
 * **возвращает ту же конфигурацию** — чтобы лист нарисовал свою кнопку из неё.
 *
 * Зачем возвращает: MainButton прижата к нижней кромке окна Telegram, её легко
 * не заметить (проверено на «Новой категории», которую пользователь не нашёл),
 * а вне Telegram её нет вовсе — то есть сохранить было физически нечем.
 * Дублировать конфиг руками в каждом листе нельзя: два описания одной кнопки
 * неизбежно разъедутся. Поэтому источник один, а `<SheetCta {...cta} />`
 * просто отражает его.
 *
 * Если SDK недоступен (браузер вне Telegram) — нативная часть no-op, а
 * возвращённая конфигурация продолжает работать.
 *
 * Раньше onClick попадал в deps useEffect, и каждый рендер дёргал hide()+show()
 * на нативной кнопке. На многополевых формах (DebtEditSheet) это копило очередь
 * анимаций и кнопка переставала реагировать. Решение: onClick хранится в ref,
 * колбэк регистрируется один раз через стабильную обёртку.
 */
export type MainButtonState = {
  text: string;
  onClick: () => void;
  enabled: boolean;
  progress: boolean;
};

export function useMainButton(opts: Options): MainButtonState {
  const { text, onClick, visible = true, enabled = true, progress = false } = opts;
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;

  // Mount/unmount: регистрируем стабильный invoker один раз, снимаем + прячем на unmount.
  useEffect(() => {
    const wa = getWebApp();
    if (!wa?.MainButton) return;
    const btn = wa.MainButton;
    const invoker = (): void => {
      onClickRef.current?.();
    };
    btn.onClick(invoker);
    return () => {
      btn.offClick(invoker);
      btn.hide();
      btn.hideProgress();
    };
  }, []);

  // Лёгкие апдейты: text/visible/enabled/progress — без re-register handler'а.
  useEffect(() => {
    const btn = getWebApp()?.MainButton;
    if (!btn) return;
    btn.setText(text);
    if (visible) btn.show();
    else btn.hide();
    if (enabled) btn.enable();
    else btn.disable();
    if (progress) btn.showProgress();
    else btn.hideProgress();
  }, [text, visible, enabled, progress]);

  return { text, onClick, enabled, progress };
}

export function useBackButton(onClick: (() => void) | null): void {
  useEffect(() => {
    const wa = getWebApp();
    if (!wa?.BackButton) return;
    const btn = wa.BackButton;
    if (!onClick) {
      btn.hide();
      return;
    }
    btn.show();
    btn.onClick(onClick);
    return () => {
      btn.offClick(onClick);
      btn.hide();
    };
  }, [onClick]);
}
